import React, { useCallback, useState } from 'react';
import { Info } from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { PALETTE, axisProps, fmtNum, tooltipStyle } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Stock in and out, straight from the movement ledger the pharmacy writes on every dispense and
// receipt. The ledger only exists since dispensing went live, so ranges are clamped to that date
// and the report says so - a silent zero would read as "nothing happened", which is wrong.

const REASON_LABEL = {
  DISPENSE: 'Dispensed to patients',
  RECEIPT: 'Received from vendors',
  RECEIPT_EDIT: 'Receipt corrections',
  RECEIPT_CANCEL: 'Receipt cancellations',
  ADJUSTMENT: 'Manual adjustments',
};

const MEDICINE_COLUMNS = [
  { key: 'medicine', label: 'Medicine' },
  { key: 'dispensed', label: 'Dispensed', align: 'right' },
  { key: 'received', label: 'Received', align: 'right' },
  { key: 'netChange', label: 'Net Change', align: 'right', render: (r) => (Number(r.netChange) >= 0 ? `+${r.netChange}` : String(r.netChange)) },
  { key: 'movements', label: 'Movements', align: 'right' },
];

const REASON_COLUMNS = [
  { key: 'reason', label: 'Movement Type', render: (r) => REASON_LABEL[r.reason] || r.reason, csv: (r) => REASON_LABEL[r.reason] || r.reason },
  { key: 'movements', label: 'Movements', align: 'right' },
  { key: 'netUnits', label: 'Net Units', align: 'right', render: (r) => (Number(r.netUnits) >= 0 ? `+${r.netUnits}` : String(r.netUnits)) },
];

const ConsumptionReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('last30'));
  const [preset, setPreset] = useState('last30');
  const [granularity, setGranularity] = useState('daily');

  const fetcher = useCallback(
    () => reportsAPI.getConsumption({ from: range.from, to: range.to, granularity }),
    [range, granularity]
  );
  const { data, loading } = useReport(fetcher);

  const totals = (data?.byReason || []).reduce(
    (acc, r) => {
      if (r.reason === 'DISPENSE') acc.dispensed = Math.abs(Number(r.netUnits));
      if (r.reason === 'RECEIPT') acc.received = Number(r.netUnits);
      return acc;
    },
    { dispensed: 0, received: 0 }
  );

  return (
    <div className="space-y-5">
      <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />

      {data?.rangeClamped && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2 text-sm text-amber-800">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Stock movements are only recorded since {data.dataFloorDate}, so the report starts there.
          Dispensing before that date went unrecorded and cannot be reported.
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          {['daily', 'monthly'].map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                granularity === g ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500">
          {loading ? '' : `${fmtNum(totals.dispensed)} units dispensed · ${fmtNum(totals.received)} received`}
        </p>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <ChartPanel title="Units in and out">
        {(data?.series || []).length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">No stock movements in the selected range</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.series} margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
              <XAxis dataKey="bucket" {...axisProps} />
              <YAxis {...axisProps} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Legend />
              <Area type="monotone" dataKey="received" name="Received" stroke={PALETTE[1]} fill={PALETTE[1]} fillOpacity={0.35} />
              <Area type="monotone" dataKey="dispensed" name="Dispensed" stroke={PALETTE[0]} fill={PALETTE[0]} fillOpacity={0.5} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'stockMedicine', value: r.medicine })}
        title="Movement by medicine"
        columns={MEDICINE_COLUMNS}
        rows={data?.byMedicine || []}
        csvName={`consumption-${range.from}-to-${range.to}`}
        emptyText="No stock movements in the selected range"
      />
      <ReportTable
        onRowClick={(r) => drill.open({ type: 'movementReason', value: r.reason, from: range.from, to: range.to })}
        title="Movement mix"
        columns={REASON_COLUMNS}
        rows={data?.byReason || []}
        csvName={`movement-mix-${range.from}-to-${range.to}`}
        searchable={false}
        emptyText="No stock movements in the selected range"
      />

      <ReportPrintLayout
        title="Consumption & Movement"
        subtitle={`Period: ${data?.range?.from || range.from} to ${data?.range?.to || range.to}`}
        tables={[
          { title: 'Movement by medicine', columns: MEDICINE_COLUMNS, rows: data?.byMedicine || [] },
          { title: 'Movement mix', columns: REASON_COLUMNS, rows: data?.byReason || [] },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default ConsumptionReport;
